import { ArrowLeftOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tabs,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { listCountries } from '../api/countries';
import type { Country } from '../api/countries';
import { listCurrencies } from '../api/currencies';
import type { Currency } from '../api/currencies';
import {
  CustomerLevel,
  changeCustomerCode,
  changeCustomerStatus,
  deleteCustomer,
  getCustomer,
  listCustomers,
  moveCustomer,
} from '../api/customers';
import type { CustomerDetail, CustomerListItem } from '../api/customers';
import { ApiError } from '../api/errors';
import { listTaxRates } from '../api/taxRates';
import type { TaxRate } from '../api/taxRates';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { CustomerCommerceSection } from './customers/CustomerCommerceSection';
import { CustomerIdentitySection } from './customers/CustomerIdentitySection';
import { CustomerOwnerCard } from './customers/CustomerOwnerCard';
import {
  indentFor,
  levelLabel,
  statusOptions,
  statusTag,
} from './customers/customerDisplay';

/** Giá trị của hộp đổi mã khách hàng. */
interface CodeFormValues {
  readonly code: string;
}

/** Giá trị của hộp đổi trạng thái. */
interface StatusFormValues {
  readonly status: number;
  readonly reason: string | null;
}

/** Giá trị của hộp di chuyển trong cây. */
interface MoveFormValues {
  readonly newParentId: string | null;
  readonly sortOrder: number;
}

/**
 * Số bản ghi nạp cho các ô chọn.
 *
 * Bằng giới hạn lớn nhất của máy chủ. Xem ghi chú cùng nội dung ở trang danh sách khách hàng.
 */
const lookupPageSize = 200;

/** Ba trạng thái chặn việc lập chứng từ mới. Đổi sang hoặc rời khỏi chúng đều phải ghi lý do. */
const blockingStatuses: readonly number[] = [3, 4, 5];

/**
 * Trang chi tiết một khách hàng.
 *
 * **Bố cục theo nhóm quyền, không theo nhóm dữ liệu.** Mỗi thẻ trên trang này tương ứng đúng một
 * điểm cuối của máy chủ và đúng một quyền. Đó là lý do trang không có một nút *Lưu* chung: người có
 * quyền sửa số điện thoại nhưng không có quyền sửa mã số thuế phải thấy rõ mình sửa được gì, thay vì
 * bấm một nút lưu chung rồi nhận thông báo từ chối mà không biết vì trường nào.
 *
 * Ba thao tác nguy hiểm — đổi trạng thái, di chuyển trong cây, xóa — nằm riêng ở đầu trang và đều đi
 * qua một hộp thoại xác nhận, vì cả ba có hậu quả ngoài phạm vi bản ghi này.
 */
export function CustomerDetailPage(): ReactNode {
  const { customerId } = useParams<{ readonly customerId: string }>();
  const navigate = useNavigate();
  const { hasPermission, hasCompanyWideDataScope } = useAuth();

  const canChangeStatus = hasPermission(PermissionCodes.CustomerChangeStatus);
  const canChangeCode = hasPermission(PermissionCodes.CustomerChangeCode);
  const canDelete = hasPermission(PermissionCodes.CustomerDelete);

  /*
    Di chuyển trong cây cần CẢ quyền chức năng VÀ phạm vi dữ liệu toàn pháp nhân — hai lớp phân quyền
    khác nhau. Lý do lớp thứ hai: thao tác này viết lại đường dẫn của cả nhánh bên dưới, gồm cả những
    nút do người khác phụ trách và người thực hiện không thấy được.

    Ẩn nút thay vì để người dùng bấm rồi nhận từ chối. Đây KHÔNG phải một lớp bảo vệ — máy chủ kiểm lại
    cả hai điều kiện — nhưng một nút chắc chắn thất bại thì không nên hiện ra.
  */
  const canMove =
    hasPermission(PermissionCodes.CustomerMove) && hasCompanyWideDataScope;

  const [statusForm] = Form.useForm<StatusFormValues>();
  const [moveForm] = Form.useForm<MoveFormValues>();
  const [codeForm] = Form.useForm<CodeFormValues>();

  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [countries, setCountries] = useState<readonly Country[]>([]);
  const [currencies, setCurrencies] = useState<readonly Currency[]>([]);
  const [taxRates, setTaxRates] = useState<readonly TaxRate[]>([]);
  const [treeNodes, setTreeNodes] = useState<readonly CustomerListItem[]>([]);

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isCodeOpen, setIsCodeOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  // Trạng thái đang chọn trong hộp đổi trạng thái, để biết có bắt buộc ghi lý do hay không.
  const [pendingStatus, setPendingStatus] = useState<number | null>(null);

  useEffect(() => {
    if (customerId === undefined) {
      return;
    }

    const controller = new AbortController();

    void (async () => {
      try {
        const loaded = await getCustomer(customerId);

        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(null);
        setCustomer(loaded);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError
            ? error.message
            : 'Không nạp được thông tin khách hàng. Kiểm tra lại đường mạng.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, [customerId]);

  /*
    Nạp các danh mục một lần khi vào trang. Khác trang danh sách — nơi chúng chỉ cần lúc mở biểu mẫu
    khai mới — ở đây mọi thẻ sửa đều cần ô chọn ngay, vì người dùng vào trang này là để sửa.

    Chỉ lấy thuế suất đang có hiệu lực hôm nay: bảng thuế suất giữ cả chuỗi hiệu lực theo thời gian,
    và cho chọn một dòng đã hết hiệu lực làm giá trị mặc định là mở đường cho hóa đơn sai thuế.
  */
  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);

        const [countryPage, currencyPage, taxRatePage, customerPage] = await Promise.all([
          listCountries({ pageSize: lookupPageSize }),
          listCurrencies({ pageSize: lookupPageSize }),
          listTaxRates({ effectiveOn: today, pageSize: lookupPageSize }),
          listCustomers({ pageSize: lookupPageSize }),
        ]);

        if (controller.signal.aborted) {
          return;
        }

        setCountries(countryPage.items);
        setCurrencies(currencyPage.items);
        setTaxRates(taxRatePage.items);
        setTreeNodes(customerPage.items);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        void message.error(
          error instanceof ApiError ? error.message : 'Không nạp được các danh mục.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const handleSaved = useCallback((updated: CustomerDetail): void => {
    setCustomer(updated);
  }, []);

  const parentName = useMemo<string>(() => {
    if (customer?.parentId === null || customer?.parentId === undefined) {
      return '— (nút gốc)';
    }

    const parent = treeNodes.find((node) => node.id === customer.parentId);

    return parent === undefined
      ? 'Chưa nạp được tên nút cha'
      : `${parent.code} — ${parent.shortName}`;
  }, [customer, treeNodes]);

  function openStatusForm(): void {
    if (customer === null) {
      return;
    }

    setPendingStatus(customer.status);
    statusForm.setFieldsValue({ status: customer.status, reason: customer.statusReason });
    setIsStatusOpen(true);
  }

  function openMoveForm(): void {
    if (customer === null) {
      return;
    }

    moveForm.setFieldsValue({
      newParentId: customer.parentId,
      sortOrder: customer.sortOrder,
    });
    setIsMoveOpen(true);
  }

  /**
   * Đổi mã khách hàng.
   *
   * Đổi mã KHÔNG làm đứt dữ liệu — mọi nơi khác trỏ tới khách hàng bằng định danh. Cái mất là con
   * người: chứng từ đã in vẫn mang mã cũ, nên lời nhắc trong cửa sổ nói rõ điều đó.
   */
  async function handleChangeCode(values: CodeFormValues): Promise<void> {
    if (customer === null) {
      return;
    }

    setIsBusy(true);

    try {
      const updated = await changeCustomerCode(customer.id, values.code);

      void message.success(`Đã đổi mã thành ${updated.code}.`);
      setCustomer(updated);
      setIsCodeOpen(false);
    } catch (error) {
      void message.error(error instanceof ApiError ? error.message : 'Không đổi được mã.');
    } finally {
      setIsBusy(false);
    }
  }

  async function handleChangeStatus(values: StatusFormValues): Promise<void> {
    if (customer === null) {
      return;
    }

    setIsBusy(true);

    try {
      const updated = await changeCustomerStatus(customer.id, values.status, values.reason);

      void message.success('Đã đổi trạng thái.');
      setCustomer(updated);
      setIsStatusOpen(false);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không đổi được trạng thái.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function handleMove(values: MoveFormValues): Promise<void> {
    if (customer === null) {
      return;
    }

    setIsBusy(true);

    try {
      const updated = await moveCustomer(
        customer.id,
        values.newParentId,
        values.sortOrder,
      );

      void message.success('Đã di chuyển khách hàng cùng toàn bộ nhánh bên dưới.');
      setCustomer(updated);
      setIsMoveOpen(false);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không di chuyển được.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  function confirmDelete(): void {
    if (customer === null) {
      return;
    }

    Modal.confirm({
      title: `Xóa khách hàng ${customer.code}?`,
      content:
        'Bản ghi được xóa mềm nên vẫn tra lại được, nhưng nó biến mất khỏi mọi danh sách và mọi ô '
        + 'chọn. Khách hàng đã có giao dịch thì nên đổi trạng thái sang đã dừng hợp tác thay vì xóa.',
      okText: 'Xóa',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await deleteCustomer(customer.id);
          void message.success('Đã xóa khách hàng.');
          void navigate(AppRoutes.Customer);
        } catch (error) {
          void message.error(
            error instanceof ApiError ? error.message : 'Không xóa được.',
          );
        }
      },
    });
  }

  if (errorMessage !== null) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="error" showIcon message={errorMessage} />

        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            void navigate(AppRoutes.Customer);
          }}
        >
          Về danh sách khách hàng
        </Button>
      </Space>
    );
  }

  if (customer === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const reasonIsRequired =
    pendingStatus !== null
    && (blockingStatuses.includes(pendingStatus) || blockingStatuses.includes(customer.status));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space wrap>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            void navigate(AppRoutes.Customer);
          }}
        >
          Danh sách
        </Button>

        <Typography.Title level={4} style={{ margin: 0 }}>
          {customer.code} — {customer.shortName}
        </Typography.Title>

        {statusTag(customer.status)}
      </Space>

      {!customer.isOpenForBusiness && (
        <Alert
          type="error"
          showIcon
          message="Khách hàng này không lập được báo giá và hợp đồng mới"
          description={
            customer.statusReason === null
              ? 'Chưa ghi lý do.'
              : `Lý do đã ghi: ${customer.statusReason}`
          }
        />
      )}

      {customer.nonDisclosureExpiresWithinSixtyDays && (
        <Alert
          type="warning"
          showIcon
          message="Thỏa thuận bảo mật sắp hết hiệu lực"
          description={
            'Ngày hết hiệu lực: '
            + (customer.nonDisclosureExpiresOn ?? 'chưa khai')
            + '. Gửi tài liệu kỹ thuật khi thỏa thuận đã hết hiệu lực là rủi ro pháp lý.'
          }
        />
      )}

      {/*
        Tiêu đề trước đây là "Thông tin không đổi được". Mã khách hàng nay đổi được nên tiêu đề ấy
        thành sai — cấp trong cây, nút cha và ngày dự án đầu tiên thì vẫn không đổi được.
      */}
      <Card size="small" title="Thông tin nhận dạng">
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Mã khách hàng">
            <Space size={8}>
              {customer.code}
              {canChangeCode && (
                <Tooltip title="Đổi mã khách hàng">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      codeForm.setFieldsValue({ code: customer.code });
                      setIsCodeOpen(true);
                    }}
                  />
                </Tooltip>
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Cấp trong cây">
            {levelLabel(customer.level)}
          </Descriptions.Item>
          <Descriptions.Item label="Nút cha">{parentName}</Descriptions.Item>
          <Descriptions.Item label="Thứ tự trình bày">{customer.sortOrder}</Descriptions.Item>
          <Descriptions.Item label="Ngày dự án đầu tiên">
            {customer.firstProjectDate ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Trạng thái">
            {statusTag(customer.status)}
          </Descriptions.Item>
        </Descriptions>

        <Space wrap style={{ marginTop: 12 }}>
          {canChangeStatus && <Button onClick={openStatusForm}>Đổi trạng thái</Button>}

          {canMove && <Button onClick={openMoveForm}>Di chuyển trong cây</Button>}

          {canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={confirmDelete}>
              Xóa
            </Button>
          )}
        </Space>
      </Card>

      <Tabs
        items={[
          {
            key: 'dinh-danh',
            label: 'Định danh và hóa đơn',
            children: (
              <CustomerIdentitySection
                customer={customer}
                countries={countries}
                onSaved={handleSaved}
              />
            ),
          },
          {
            key: 'thuong-mai',
            label: 'Thương mại và liên lạc',
            children: (
              <CustomerCommerceSection
                customer={customer}
                currencies={currencies}
                taxRates={taxRates}
                onSaved={handleSaved}
              />
            ),
          },
          {
            key: 'phu-trach',
            label: 'Người phụ trách',
            children: <CustomerOwnerCard customer={customer} onSaved={handleSaved} />,
          },
        ]}
      />

      <Modal
        title={`Đổi mã của ${customer.code}`}
        open={isCodeOpen}
        confirmLoading={isBusy}
        onOk={() => {
          codeForm.submit();
        }}
        onCancel={() => {
          setIsCodeOpen(false);
        }}
        okText="Đổi mã"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Chứng từ đã gửi đi vẫn mang mã cũ"
          description="Đổi mã không làm đứt dữ liệu trong hệ thống: dự án, cơ hội và liên hệ đều trỏ tới khách hàng bằng định danh, không bằng mã. Nhưng báo giá hay hợp đồng đã in trước đó thì vẫn ghi mã cũ, nên hãy báo cho người đang giữ những chứng từ ấy."
        />
        <Form<CodeFormValues>
          form={codeForm}
          layout="vertical"
          requiredMark={false}
          disabled={isBusy}
          onFinish={(values) => {
            void handleChangeCode(values);
          }}
        >
          <Form.Item
            name="code"
            label="Mã mới"
            extra="Chữ in hoa không dấu, chữ số, gạch nối. Hệ thống tự viết hoa."
            rules={[{ required: true, message: 'Chưa nhập mã mới.' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Đổi trạng thái của ${customer.code}`}
        open={isStatusOpen}
        confirmLoading={isBusy}
        onOk={() => {
          statusForm.submit();
        }}
        onCancel={() => {
          setIsStatusOpen(false);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<StatusFormValues>
          form={statusForm}
          layout="vertical"
          requiredMark={false}
          disabled={isBusy}
          onFinish={(values) => {
            void handleChangeStatus(values);
          }}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Ba trạng thái chặn việc lập chứng từ mới"
            description={
              'Tạm ngừng, đã dừng hợp tác và danh sách đen đều chặn việc lập báo giá và hợp đồng '
              + 'mới. Mỗi lần vào hoặc ra khỏi những trạng thái đó đều bắt buộc ghi lý do — mở lại '
              + 'cũng là một quyết định cần người chịu trách nhiệm.'
            }
          />

          <Form.Item
            name="status"
            label="Trạng thái"
            rules={[{ required: true, message: 'Chưa chọn trạng thái.' }]}
          >
            <Select<number>
              options={[...statusOptions]}
              onChange={(value) => {
                setPendingStatus(value);
              }}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Lý do"
            extra={
              reasonIsRequired
                ? 'Bắt buộc: người gặp việc bị chặn cần biết vì sao, và người quyết định cần chịu trách nhiệm.'
                : 'Không bắt buộc khi chuyển giữa hai trạng thái đều cho giao dịch.'
            }
            rules={
              reasonIsRequired ? [{ required: true, message: 'Chưa ghi lý do.' }] : []
            }
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Di chuyển ${customer.code} trong cây`}
        open={isMoveOpen}
        confirmLoading={isBusy}
        onOk={() => {
          moveForm.submit();
        }}
        onCancel={() => {
          setIsMoveOpen(false);
        }}
        okText="Di chuyển"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<MoveFormValues>
          form={moveForm}
          layout="vertical"
          requiredMark={false}
          disabled={isBusy}
          onFinish={(values) => {
            void handleMove(values);
          }}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Toàn bộ nhánh bên dưới đi theo"
            description={
              'Chuyển một nhà máy sang pháp nhân khác đổi luôn nơi xuất hóa đơn cho nhà máy đó, và '
              + 'mọi line bên dưới đi theo trong cùng một giao dịch. Đây không phải việc sắp xếp '
              + 'cho gọn.'
            }
          />

          <Form.Item
            name="newParentId"
            label="Nút cha mới"
            extra={
              customer.level === CustomerLevel.Group
                ? 'Cấp tập đoàn luôn là nút gốc nên phải để trống.'
                : customer.level === CustomerLevel.LegalEntity
                  ? 'Để trống để đưa pháp nhân này lên làm nút gốc.'
                  : 'Bắt buộc: cấp nhà máy và cấp line không được đứng ở gốc.'
            }
          >
            <Select<string | null>
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Chọn nút cha mới"
              options={treeNodes
                .filter((node) => node.id !== customer.id)
                .map((node) => ({
                  value: node.id,
                  label: `${indentFor(node.depth)}${node.code} — ${node.shortName}`,
                }))}
            />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Thứ tự trình bày"
            rules={[{ required: true, message: 'Chưa nhập thứ tự trình bày.' }]}
          >
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
